import { useCallback } from 'react'
import {
    collection,
    addDoc,
    getDocs,
    query,
    where,
    orderBy,
    Timestamp,
} from 'firebase/firestore'
import { firestore } from '../config/firebase'
import { IOdometerRecord, OdometerRecord } from '../../../shared/types/odometer.types'
import { COLLECTIONS } from '../../../shared/constants/collections'

export interface OdometerHistoryFilters {
    /** Filtruje dle UID vozidla. */
    vehicleUid?: string
    /** Směr řazení dle data záznamu. Výchozí: 'desc'. */
    order?: 'asc' | 'desc'
}

interface IUseOdometerHistoryReturn {
    list: (filters?: OdometerHistoryFilters) => Promise<OdometerRecord[]>
    create: (data: IOdometerRecord) => Promise<OdometerRecord>
}

/**
 * Mapuje Firestore dokument na OdometerRecord.
 * Pole je v Firestore uloženo jako `createdAt`, v TypeScript exponováno jako `recordedAt`.
 */
const convertDoc = (id: string, data: Record<string, unknown>): OdometerRecord => {
    const raw = data.createdAt
    return {
        id,
        ...data,
        recordedAt: raw instanceof Timestamp
            ? raw.toDate()
            : new Date(raw as string),
    } as OdometerRecord
}

const useOdometerHistory = (): IUseOdometerHistoryReturn => {

    /**
     * Načte seznam záznamů historie odometru s volitelnými filtry.
     * @param filters - Volitelné filtry: vehicleUid, order
     */
    const list = useCallback(async (filters?: OdometerHistoryFilters): Promise<OdometerRecord[]> => {
        const constraints = []

        if (filters?.vehicleUid) {
            constraints.push(where('vehicleUid', '==', filters.vehicleUid))
        }

        constraints.push(orderBy('createdAt', filters?.order ?? 'desc'))

        const q = query(collection(firestore, COLLECTIONS.VEHICLE_ODOMETER_HISTORY), ...constraints)
        const snapshot = await getDocs(q)
        return snapshot.docs.map((d) => convertDoc(d.id, d.data()))
    }, [])

    /**
     * Vytvoří nový záznam historie odometru.
     * Pole `recordedAt` je uloženo do Firestore jako `createdAt`.
     * @param data - Data záznamu
     */
    const create = useCallback(async (data: IOdometerRecord): Promise<OdometerRecord> => {
        const { recordedAt, ...rest } = data
        const ref = await addDoc(collection(firestore, COLLECTIONS.VEHICLE_ODOMETER_HISTORY), {
            ...rest,
            createdAt: recordedAt,
        })
        return { id: ref.id, ...data }
    }, [])

    return { list, create }
}

export default useOdometerHistory
